import React, { useEffect, useState } from 'react';
import CreditCheckModal from './CreditCheckModal';
import { FaEye } from 'react-icons/fa';
import '../../../styles/dashboard/OrgUser/CreditCheck.css';
import OrgUserService from '../../../services/orgUser.service';
import OrgUserAuthService from '../../../services/orgUserAuth.service';
import PdfViewerModal from '../../PdfViewerModal/PdfViewerModal';

export default function CreditCheckOrgUser({ mode = 'default', selectedPlan = null }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [checks, setChecks] = useState([]);
  const [selectedCheck, setSelectedCheck] = useState(null);
  const [activeTab, setActiveTab] = useState('plans'); // 'plans' or 'history'
  const [modalOpen, setModalOpen] = useState(false);
  const [modalPlan, setModalPlan] = useState('Basic');
  const [selectedProvider, setSelectedProvider] = useState(null); // CIBIL, CRIF, or Experian
  const [showProviderSelection, setShowProviderSelection] = useState(false);
  const [creditCosts, setCreditCosts] = useState({ 
    basicCreditCost: 100, 
    advancedCreditCost: 150,
    comboCreditCost: 200,
    // Provider-specific costs for WITHOUT OTP mode
    advancedCreditCostCIBIL: 25,
    advancedCreditCostCRIF: 22,
    advancedCreditCostExperian: 23,
    comboCreditCostCIBIL: 40,
    comboCreditCostCRIF: 38,
    comboCreditCostExperian: 39,
    // Equifax for WITH OTP mode
    advancedCreditCostEquifax: 20,
    comboCreditCostEquifax: 35
  });
  const [isPdfViewerOpen, setIsPdfViewerOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const checksPerPage = 10;
  
  // Don't filter on client side since we're using server-side pagination
  const displayChecks = checks;

  // Determine page title based on mode
  const getPageTitle = () => {
    if (mode === 'with-otp') return 'Credit Score Checks ';
    if (mode === 'without-otp') return 'Credit Score Checks ';
    return 'Credit Score Checks';
  };

  const getPageDescription = () => {
    if (mode === 'with-otp') return 'Manage credit verification requests with OTP authentication';
    if (mode === 'without-otp') return 'Manage credit verification requests without OTP authentication';
    return 'Manage and view all credit verification requests';
  };

  const handleViewDetails = async (check) => {
    setSelectedCheck(check);
    setPdfUrl(null); // Clear previous PDF URL
    
    // Function to try loading PDF with retry logic
    const loadPdfWithRetry = async (retryCount = 0, maxRetries = 5) => {
      try {
        const token = OrgUserAuthService.getAuthHeader();
        const apiUrl = `${process.env.REACT_APP_API_URL}/orguser/credit-checks/${check.id}/download`;
        const response = await fetch(apiUrl, { headers: token });
        const payload = await response.json();
        
        // Check if PDF is still being generated (202 status)
        if (response.status === 202 || payload.pdfReady === false) {
          if (retryCount < maxRetries) {
            // eslint-disable-next-line no-console
            console.log(`PDF not ready yet, retrying in 2 seconds... (attempt ${retryCount + 1}/${maxRetries})`);
            setTimeout(() => loadPdfWithRetry(retryCount + 1, maxRetries), 2000);
          } else {
            // eslint-disable-next-line no-console
            console.warn('PDF generation is taking longer than expected. Please try again later.');
          }
          return;
        }
        
        const url = payload?.data?.url || payload?.url || payload?.downloadUrl || payload?.pdfUrl;
        if (url) {
          setPdfUrl(url);
          // eslint-disable-next-line no-console
          console.log('PDF URL loaded successfully');
        } else {
          // eslint-disable-next-line no-console
          console.warn('No PDF URL in response:', payload);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to load PDF URL for details:', err);
      }
    };
    
    // Start loading PDF
    loadPdfWithRetry();
  };

  const handleOpenPdfFromDetails = () => {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank');
    } else {
      alert('PDF is still being generated. Please wait a moment and try again.');
    }
  };

  const loadChecks = async () => {
    setLoading(true);
    try {
      const res = await OrgUserService.getCreditChecks(
        currentPage, 
        checksPerPage, 
        searchTerm, 
        statusFilter === 'all' ? '' : statusFilter, 
        fromDate, 
        toDate
      );
      // Backend returns { message, data, pagination }
      const list = res.data || res?.data?.data || res?.data || [];
      setChecks(list);
      
      // Update total records from pagination data
      const total = res.pagination?.totalRecords || res.pagination?.total || list.length;
      setTotalRecords(total);
      
      // eslint-disable-next-line no-console
      console.log('📊 Pagination Data:', { 
        currentPage, 
        checksPerPage, 
        totalRecords: total, 
        recordsFetched: list.length,
        totalPages: Math.ceil(total / checksPerPage)
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to load credit checks', err);
      setChecks([]);
      setTotalRecords(0);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (activeTab === 'history') {
      loadChecks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, currentPage, searchTerm, statusFilter, fromDate, toDate]);

  useEffect(() => {
    const loadCosts = async () => {
      try {
        const res = await OrgUserService.getCreditCosts();
        if (res?.data) {
          setCreditCosts(res.data);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to load credit costs', err);
      }
    };
    loadCosts();
  }, []);

  // Switch to plans tab when a specific plan is selected from sidebar
  useEffect(() => {
    if (selectedPlan) {
      setActiveTab('plans');
    }
  }, [selectedPlan]);

  const handlePlanSelection = (plan) => {
    setModalPlan(plan);
    // For "without-otp" mode and  Advance/Combo plans, show provider selection
    if (mode === 'without-otp' && (plan === 'Advanced' || plan === 'Combo')) {
      setShowProviderSelection(true);
    } else {
      // For Basic plan or "with-otp" mode, open modal directly
      setSelectedProvider(mode === 'with-otp' ? 'equifax' : null);
      setModalOpen(true);
    }
  };

  const handleProviderSelection = (provider) => {
    setSelectedProvider(provider);
    setShowProviderSelection(false);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedProvider(null);
  };

  const handleCloseProviderSelection = () => {
    setShowProviderSelection(false);
    setModalPlan('Basic');
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (fromDate) params.append('fromDate', fromDate);
      if (toDate) params.append('toDate', toDate);

      const url = `${process.env.REACT_APP_API_URL}/orguser/credit-checks/export?${params.toString()}`;
      const res = await fetch(url, {
        headers: OrgUserAuthService.getAuthHeader(),
      });
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = 'credit-checks.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to export credit checks', err);
    }
  };



  return (
    <div className="credit-checks-page">
      <div className="page-header">
        <div>
          <h1>{getPageTitle()}</h1>
          <p>{getPageDescription()}</p>
        </div>
        {/* <button className="btn-primary">➕ New Credit Check</button> */}
      </div>

      {/* Tabs */}
      <div className="credit-check-tabs-container">
        <div className="credit-check-tabs-wrapper">
          <button
            className={`credit-check-tab-button ${activeTab === 'plans' ? 'active' : 'inactive'}`}
            onClick={() => setActiveTab('plans')}
          >
            Check Plans
          </button>
          <button
            className={`credit-check-tab-button ${activeTab === 'history' ? 'active' : 'inactive'}`}
            onClick={() => setActiveTab('history')}
          >
            History
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'plans' && (
        <>
          <div className="checks-grid credit-check-plans-grid">
            {/* Basic Plan Card */}
            {(!selectedPlan || selectedPlan === 'Basic') && (
              <div className="check-card basic-plan">
                <div className="check-card-header">
                  <div>
                    <div className="check-card-title">Basic Report</div>
                    <div className="check-id">For simple credit checks</div>
                  </div>
                  <span className="plan-badge basic">Basic</span>
                </div>
                <div className="check-card-body">
                  <div className="check-info-row">
                    <span className="check-info-label">Cost</span>
                    <span className="check-info-value">₹{creditCosts.basicCreditCost}</span>
                  </div>
                  <div className="check-info-row">
                    <span className="check-info-label">Features</span>
                    <span className="check-info-value">LCS Report & Pan Verification</span>
                  </div>
                  <div className="check-info-row">
                    <span className="check-info-label">Report details Received from</span>
                    <span className="check-info-value">Internal System</span>
                  </div>
                </div>
                <div className="check-card-footer">
                  <button className="btn-view" onClick={() => handlePlanSelection('Basic')}>Check Now</button>
                </div>
              </div>
            )}

            {/* Advance Plan Card - with-otp (Equifax) */}
            {mode === 'with-otp' && (!selectedPlan || selectedPlan === 'Advanced') && (
              <div className="check-card advance-plan">
                <div className="check-card-header">
                  <div>
                    <div className="check-card-title">Advance Report</div>
                    <div className="check-id">For detailed credit checks</div>
                  </div>
                  <span className="plan-badge advance">Advance</span>
                </div>
                <div className="check-card-body">
                  <div className="check-info-row">
                    <span className="check-info-label">Cost</span>
                    <span className="check-info-value">₹{creditCosts.advancedCreditCostEquifax || creditCosts.advancedCreditCost}</span>
                  </div>
                  <div className="check-info-row">
                    <span className="check-info-label">Features</span>
                    <span className="check-info-value">Credit Bureau Report</span>
                  </div>
                  <div className="check-info-row">
                    <span className="check-info-label">Report details Received from</span>
                    <span className="check-info-value api-provider-equifax">Credit Score (With OTP)</span>
                  </div>
                </div>
                <div className="check-card-footer">
                  <button className="btn-view" onClick={() => handlePlanSelection('Advanced')}>Check Now</button>
                </div>
              </div>
            )}

            {/* Advance Plan Cards - without-otp (CIBIL/CRIF/Experian) */}
            {mode === 'without-otp' && (!selectedPlan || selectedPlan === 'Advanced') && (
              <>
                {/* CIBIL Card */}
                <div className="check-card advance-plan cibil-api-card">
                  <div className="check-card-header">
                    <div>
                      <div className="check-card-title">Advance Report - CIBIL</div>
                      <div className="check-id">TransUnion CIBIL Bureau</div>
                    </div>
                    <span className="plan-badge advance">Advance</span>
                  </div>
                  <div className="check-card-body">
                    <div className="check-info-row">
                      <span className="check-info-label">Cost</span>
                      <span className="check-info-value">₹{creditCosts.advancedCreditCostCIBIL}</span>
                    </div>
                    <div className="check-info-row">
                      <span className="check-info-label">Features</span>
                      <span className="check-info-value">Credit Bureau Report</span>
                    </div>
                    <div className="check-info-row">
                      <span className="check-info-label">Report details Received from</span>
                      <span className="check-info-value api-provider-cibil">TransUnion CIBIL</span>
                    </div>
                  </div>
                  <div className="check-card-footer">
                    <button className="btn-view" onClick={() => { setModalPlan('Advanced'); handleProviderSelection('cibil'); }}>Check Now</button>
                  </div>
                </div>

                {/* CRIF Card - hidden */}
                {/* <div className="check-card advance-plan crif-api-card">
                  <div className="check-card-header">
                    <div>
                      <div className="check-card-title">Advance Report - CRIF</div>
                      <div className="check-id">CRIF Highmark Bureau</div>
                    </div>
                    <span className="plan-badge advance">Advance</span>
                  </div>
                  <div className="check-card-body">
                    <div className="check-info-row">
                      <span className="check-info-label">Cost</span>
                      <span className="check-info-value">₹{creditCosts.advancedCreditCostCRIF}</span>
                    </div>
                    <div className="check-info-row">
                      <span className="check-info-label">Features</span>
                      <span className="check-info-value">Credit Bureau Report</span>
                    </div>
                    <div className="check-info-row">
                      <span className="check-info-label">Report details Received from</span>
                      <span className="check-info-value api-provider-crif">CRIF Highmark</span>
                    </div>
                  </div>
                  <div className="check-card-footer">
                    <button className="btn-view" onClick={() => { setModalPlan('Advanced'); handleProviderSelection('crif'); }}>Check Now</button>
                  </div>
                </div> */}

                {/* Experian Card - hidden */}
                {/* <div className="check-card advance-plan experian-api-card">
                  <div className="check-card-header">
                    <div>
                      <div className="check-card-title">Advance Report - Experian</div>
                      <div className="check-id">Experian Bureau</div>
                    </div>
                    <span className="plan-badge advance">Advance</span>
                  </div>
                  <div className="check-card-body">
                    <div className="check-info-row">
                      <span className="check-info-label">Cost</span>
                      <span className="check-info-value">₹{creditCosts.advancedCreditCostExperian}</span>
                    </div>
                    <div className="check-info-row">
                      <span className="check-info-label">Features</span>
                      <span className="check-info-value">Credit Bureau Report</span>
                    </div>
                    <div className="check-info-row">
                      <span className="check-info-label">Report details Received from</span>
                      <span className="check-info-value api-provider-experian">Experian</span>
                    </div>
                  </div>
                  <div className="check-card-footer">
                    <button className="btn-view" onClick={() => { setModalPlan('Advanced'); handleProviderSelection('experian'); }}>Check Now</button>
                  </div>
                </div> */}
              </>
            )}

            {/* Combo Plan Card - with-otp */}
            {mode === 'with-otp' && (!selectedPlan || selectedPlan === 'Combo') && (
              <div className="check-card combo-plan">
                <div className="check-card-header">
                  <div>
                    <div className="check-card-title">Combo Report</div>
                    <div className="check-id">All-in-one credit check</div>
                  </div>
                  <span className="plan-badge combo">Combo</span>
                </div>
                <div className="check-card-body">
                  <div className="check-info-row">
                    <span className="check-info-label">Cost</span>
                    <span className="check-info-value">₹{creditCosts.comboCreditCostEquifax || creditCosts.comboCreditCost}</span>
                  </div>
                  <div className="check-info-row">
                    <span className="check-info-label">Features</span>
                    <span className="check-info-value">
                      LCS Report, Pan Verification,<br/>
                      Credit Bureau + Optional Court Case
                    </span>
                  </div>
                  <div className="check-info-row">
                    <span className="check-info-label">Report details Received from</span>
                    <span className="check-info-value api-provider-equifax">Credit Score (With OTP)</span>
                  </div>
                  <div className="check-info-row">
                    <span className="check-info-label">Court Case</span>
                    <span className="check-info-value">Optional</span>
                  </div>
                </div>
                <div className="check-card-footer">
                  <button className="btn-view" onClick={() => handlePlanSelection('Combo')}>Check Now</button>
                </div>
              </div>
            )}

            {/* Combo Plan Cards - without-otp */}
            {mode === 'without-otp' && (!selectedPlan || selectedPlan === 'Combo') && (
              <>
                {/* CIBIL Combo */}
                <div className="check-card combo-plan cibil-api-card">
                  <div className="check-card-header">
                    <div>
                      <div className="check-card-title">Combo Report - CIBIL</div>
                      <div className="check-id">Complete verification with CIBIL</div>
                    </div>
                    <span className="plan-badge combo">Combo</span>
                  </div>
                  <div className="check-card-body">
                    <div className="check-info-row">
                      <span className="check-info-label">Cost</span>
                      <span className="check-info-value">₹{creditCosts.comboCreditCostCIBIL}</span>
                    </div>
                    <div className="check-info-row">
                      <span className="check-info-label">Features</span>
                      <span className="check-info-value">
                        LCS Report, Pan Verification,<br/>
                        Credit Bureau + Optional Court Case
                      </span>
                    </div>
                    <div className="check-info-row">
                      <span className="check-info-label">Report details Received from</span>
                      <span className="check-info-value api-provider-cibil">TransUnion CIBIL</span>
                    </div>
                    <div className="check-info-row">
                      <span className="check-info-label">Court Case</span>
                      <span className="check-info-value">Optional</span>
                    </div>
                  </div>
                  <div className="check-card-footer">
                    <button className="btn-view" onClick={() => { setModalPlan('Combo'); handleProviderSelection('cibil'); }}>Check Now</button>
                  </div>
                </div>

                {/* CRIF Combo - hidden */}
                {/* <div className="check-card combo-plan crif-api-card">
                  <div className="check-card-header">
                    <div>
                      <div className="check-card-title">Combo Report - CRIF</div>
                      <div className="check-id">Complete verification with CRIF</div>
                    </div>
                    <span className="plan-badge combo">Combo</span>
                  </div>
                  <div className="check-card-body">
                    <div className="check-info-row">
                      <span className="check-info-label">Cost</span>
                      <span className="check-info-value">₹{creditCosts.comboCreditCostCRIF}</span>
                    </div>
                    <div className="check-info-row">
                      <span className="check-info-label">Features</span>
                      <span className="check-info-value">
                        LCS Report, Pan Verification,<br/>
                        Credit Bureau + Optional Court Case
                      </span>
                    </div>
                    <div className="check-info-row">
                      <span className="check-info-label">Report details Received from</span>
                      <span className="check-info-value api-provider-crif">CRIF Highmark</span>
                    </div>
                    <div className="check-info-row">
                      <span className="check-info-label">Court Case</span>
                      <span className="check-info-value">Optional</span>
                    </div>
                  </div>
                  <div className="check-card-footer">
                    <button className="btn-view" onClick={() => { setModalPlan('Combo'); handleProviderSelection('crif'); }}>Check Now</button>
                  </div>
                </div> */}

                {/* Experian Combo - hidden */}
                {/* <div className="check-card combo-plan experian-api-card">
                  <div className="check-card-header">
                    <div>
                      <div className="check-card-title">Combo Report - Experian</div>
                      <div className="check-id">Complete verification with Experian</div>
                    </div>
                    <span className="plan-badge combo">Combo</span>
                  </div>
                  <div className="check-card-body">
                    <div className="check-info-row">
                      <span className="check-info-label">Cost</span>
                      <span className="check-info-value">₹{creditCosts.comboCreditCostExperian}</span>
                    </div>
                    <div className="check-info-row">
                      <span className="check-info-label">Features</span>
                      <span className="check-info-value">
                        LCS Report, Pan Verification,<br/>
                        Credit Bureau + Optional Court Case
                      </span>
                    </div>
                    <div className="check-info-row">
                      <span className="check-info-label">Report details Received from</span>
                      <span className="check-info-value api-provider-experian">Experian</span>
                    </div>
                    <div className="check-info-row">
                      <span className="check-info-label">Court Case</span>
                      <span className="check-info-value">Optional</span>
                    </div>
                  </div>
                  <div className="check-card-footer">
                    <button className="btn-view" onClick={() => { setModalPlan('Combo'); handleProviderSelection('experian'); }}>Check Now</button>
                  </div>
                </div> */}
              </>
            )}
          </div>
        </>
      )}

  {/* Provider Selection Modal for WITHOUT OTP - Advance/Combo */}
  {showProviderSelection && (
    <div className="modal-overlay" onClick={handleCloseProviderSelection}>
      <div className="modal-content provider-selection-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Select Bureau Provider - {modalPlan} Plan</h2>
          <button className="btn-close" onClick={handleCloseProviderSelection}>×</button>
        </div>
        <div className="modal-body">
          <p className="provider-selection-description">
            Choose your preferred credit bureau for {modalPlan} plan verification
          </p>
          <div className="provider-cards-grid">
            {/* CIBIL Card */}
            <div className="provider-card cibil-card" onClick={() => handleProviderSelection('cibil')}>
              <div className="provider-card-header">
                <h3>TransUnion CIBIL</h3>
                <span className="provider-badge">Popular</span>
              </div>
              <div className="provider-card-body">
                <div className="provider-cost">
                  ₹{modalPlan === 'Advanced' ? creditCosts.advancedCreditCostCIBIL : creditCosts.comboCreditCostCIBIL}
                </div>
                <ul className="provider-features">
                  <li>Most widely used in India</li>
                  <li>Comprehensive credit history</li>
                  <li>Fast report generation</li>
                </ul>
              </div>
              <div className="provider-card-footer">
                <button className="btn-provider-select">Select CIBIL</button>
              </div>
            </div>

            {/* CRIF Card - hidden */}
            {/* <div className="provider-card crif-card" onClick={() => handleProviderSelection('crif')}>
              <div className="provider-card-header">
                <h3>CRIF Highmark</h3>
                <span className="provider-badge">Recommended</span>
              </div>
              <div className="provider-card-body">
                <div className="provider-cost">
                  ₹{modalPlan === 'Advanced' ? creditCosts.advancedCreditCostCRIF : creditCosts.comboCreditCostCRIF}
                </div>
                <ul className="provider-features">
                  <li>Detailed credit analysis</li>
                  <li>Includes microfinance data</li>
                  <li>Competitive pricing</li>
                </ul>
              </div>
              <div className="provider-card-footer">
                <button className="btn-provider-select">Select CRIF</button>
              </div>
            </div> */}

            {/* Experian Card - hidden */}
            {/* <div className="provider-card experian-card" onClick={() => handleProviderSelection('experian')}>
              <div className="provider-card-header">
                <h3>Experian</h3>
                <span className="provider-badge">Global Leader</span>
              </div>
              <div className="provider-card-body">
                <div className="provider-cost">
                  ₹{modalPlan === 'Advanced' ? creditCosts.advancedCreditCostExperian : creditCosts.comboCreditCostExperian}
                </div>
                <ul className="provider-features">
                  <li>International credit bureau</li>
                  <li>Advanced fraud detection</li>
                  <li>Detailed reports</li>
                </ul>
              </div>
              <div className="provider-card-footer">
                <button className="btn-provider-select">Select Experian</button>
              </div>
            </div> */}
          </div>
        </div>
      </div>
    </div>
  )}

  {/* Credit Check Modal - Pass mode and selected provider to modal */}
  <CreditCheckModal 
    open={modalOpen} 
    onClose={handleCloseModal} 
    planType={modalPlan}
    mode={mode}
    selectedProvider={selectedProvider}
  />
      {activeTab === 'history' && (
        <>
          <div className="search-filter-bar">
            <div className="search-filter-row">
              <div className="search-input-wrapper">
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search by customer name or PAN..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1); // Reset to first page on search
                  }}
                />
              </div>
              <select 
                className="filter-select" 
                value={statusFilter} 
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1); // Reset to first page on filter change
                }}
              >
                <option value="all">All Status</option>
                <option value="completed">Completed</option>
                <option value="pending">Pending</option>
              </select>
              <input
                type="date"
                className="filter-date"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setCurrentPage(1); // Reset to first page on date change
                }}
              />
              <input
                type="date"
                className="filter-date"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setCurrentPage(1); // Reset to first page on date change
                }}
              />
              <button className="btn-primary" onClick={handleExport} disabled={loading || !checks.length}>
                Export Excel
              </button>
            </div>
          </div>

          <div className="table-container">
            {loading && <div className="loading-message">Loading credit checks...</div>}
            {!loading && displayChecks.length === 0 && (
              <div className="empty-state">
                <p>No credit checks found</p>
              </div>
            )}
            {!loading && displayChecks.length > 0 && (
              <>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Check ID</th>
                      <th>Customer Name</th>
                      <th>Plan Type</th>
                      {/* <th>CIBIL Score</th> */}
                      <th>Performed By</th>
                      <th>Status</th>
                      <th>Cost</th>
                      <th>Date</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayChecks.map((check) => (
                      <tr key={check.id}>
                        <td>{check.id}</td>
                        <td>{check.customerName}</td>
                        <td><span className="plan-badge">{check.planType}</span></td>
                        {/* <td><span className="score-value">{check.cibilScore}</span></td> */}
                        <td>{check.performedByName || '-'}</td>
                        <td><span className={`status-badge ${check.status}`}>{check.status}</span></td>
                        <td>₹{check.creditCost}</td>
                        <td>{new Date(check.createdAt).toLocaleDateString()}</td>
                        <td>
                          <button className="btn-icon-view" onClick={() => handleViewDetails(check)} title="View Details">
                            <FaEye />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination - Always show if there are records */}
                {totalRecords > 0 && (
                  <div className="pagination-container">
                    <div className="pagination-info">
                      Showing {(currentPage - 1) * checksPerPage + 1} to{" "}
                      {Math.min(currentPage * checksPerPage, totalRecords)} of {totalRecords}{" "}
                      records
                    </div>
                    <div className="pagination-buttons">
                      <button
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(currentPage - 1)}
                        className="pagination-btn"
                      >
                        Previous
                      </button>
                      {Array.from({ length: Math.ceil(totalRecords / checksPerPage) }).map((_, i) => {
                        const pageNum = i + 1;
                        const totalPages = Math.ceil(totalRecords / checksPerPage);
                        // Show first, last, current, and adjacent pages
                        if (
                          pageNum === 1 ||
                          pageNum === totalPages ||
                          (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                        ) {
                          return (
                            <button
                              key={pageNum}
                              className={`pagination-btn ${currentPage === pageNum ? "active" : ""}`}
                              onClick={() => setCurrentPage(pageNum)}
                            >
                              {pageNum}
                            </button>
                          );
                        } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                          return <span key={pageNum}>...</span>;
                        }
                        return null;
                      })}
                      <button
                        disabled={currentPage === Math.ceil(totalRecords / checksPerPage)}
                        onClick={() => setCurrentPage(currentPage + 1)}
                        className="pagination-btn"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}


      {selectedCheck && (
        <div className="modal-overlay" onClick={() => setSelectedCheck(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Credit Check Details</h2>
              <button className="btn-close" onClick={() => setSelectedCheck(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="details-grid">
                <div className="detail-item">
                  <span className="detail-label">Check ID</span>
                  <span className="detail-value">{selectedCheck.id}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Customer Name</span>
                  <span className="detail-value">{selectedCheck.customerName || selectedCheck.entityName}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Plan Type</span>
                  <span className="detail-value">{selectedCheck.planType}</span>
                </div>
                {/* <div className="detail-item">
                  <span className="detail-label">CIBIL Score</span>
                  <span className="detail-value">{selectedCheck.cibilScore ?? '-'}</span>
                </div> */}
                <div className="detail-item">
                  <span className="detail-label">Status</span>
                  <span className={`status-badge ${selectedCheck.status}`}>{selectedCheck.status}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Credit Cost</span>
                  <span className="detail-value">₹{selectedCheck.creditCost}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">PAN Number</span>
                  <span className="detail-value">{selectedCheck.panNumber || '-'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Aadhaar</span>
                  <span className="detail-value">{selectedCheck.aadhaarNumber || selectedCheck.aadhaar || '-'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Loan Amount</span>
                  <span className="detail-value">₹{selectedCheck.loanAmount != null ? Number(selectedCheck.loanAmount).toLocaleString() : '-'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Performed By</span>
                  <span className="detail-value">{selectedCheck.performedByName || '-'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Created At</span>
                  <span className="detail-value">{selectedCheck.createdAt ? new Date(selectedCheck.createdAt).toLocaleString() : '-'}</span>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              {pdfUrl && (
                <button className="btn-primary" onClick={handleOpenPdfFromDetails}>
                  View PDF Report
                </button>
              )}
              <button className="btn-secondary" onClick={() => setSelectedCheck(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      <PdfViewerModal
        isOpen={isPdfViewerOpen}
        toggle={() => setIsPdfViewerOpen(false)}
        fileUrl={pdfUrl}
        title="Credit Assessment Report"
      />
    </div>
  );
}

